import React, { Suspense } from 'react';
import { Box, Button, Callout, Card, Flex, Heading, Tabs, Text, TextField } from '@radix-ui/themes';
import HomeTab from './components/tabs/HomeTab';
import PastWinnersTab from './components/tabs/PastWinnersTab';
import MembersTab from './components/tabs/MembersTab';
import VedtaegterTab from './components/tabs/VedtaegterTab';
import MaterialIcon from './components/common/MaterialIcon';
import { useAuth } from './hooks/useAuth';
import { useManagedUsers } from './hooks/useManagedUsers';
import { useMembers } from './hooks/useMembers';
import { useWeights } from './hooks/useWeights';
import { validateImageFile } from './utils/imageValidation';

const CalendarSection = React.lazy(() => import('./components/sections/CalendarSection'));
const ReferatSection = React.lazy(() => import('./components/sections/ReferatSection'));
const GallerySection = React.lazy(() => import('./components/sections/GallerySection'));
const SuggestionsSection = React.lazy(() => import('./components/sections/SuggestionsSection'));
const WeightTab = React.lazy(() => import('./components/tabs/WeightTab'));
const AdminPanel = React.lazy(() => import('./components/views/AdminPanel'));

function TabLoadingFallback() {
  return (
    <Flex justify="center" align="center" className="py-12">
      <Text size="2" color="gray" className="inline-flex items-center gap-2">
        <MaterialIcon name="hourglass_top" className="text-[20px] text-pine-700" />
        Henter...
      </Text>
    </Flex>
  );
}

function App() {
  const {
    email,
    setEmail,
    password,
    setPassword,
    isRegisterMode,
    setIsRegisterMode,
    currentUser,
    isLoadingUser,
    isSubmitting,
    authError,
    setAuthError,
    whitelistStatus,
    isAdmin,
    isCheckingAdmin,
    view,
    setView,
    hasAppAccess,
    handleEmailAuth,
    handleGoogleSignIn,
    handleSignOut,
    handleRefreshAccess,
  } = useAuth();

  const {
    managedUsers,
    isLoadingManagedUsers,
    adminError,
    adminSuccess,
    fetchManagedUsers,
    handleSetWhitelist,
    handleSetUserMemberLink,
    handleDeleteUser,
  } = useManagedUsers(currentUser, isAdmin, view);

  const {
    members,
    isLoadingMembers,
    membersError,
    setMembersError,
    memberSuccess,
    memberName,
    setMemberName,
    memberRole,
    setMemberRole,
    memberDescription,
    setMemberDescription,
    setMemberImage,
    isSubmittingMember,
    editingMemberId,
    editName,
    setEditName,
    editRole,
    setEditRole,
    editDescription,
    setEditDescription,
    setEditImage,
    isUpdatingMember,
    isDeletingMember,
    getRoleLabel,
    memberNameById,
    memberById,
    memberAvatarById,
    handleCreateMember,
    startEditMember,
    cancelEditMember,
    handleUpdateMember,
    handleDeleteMember,
  } = useMembers(currentUser, hasAppAccess, isAdmin);

  const {
    isLoadingWeights,
    weightsError,
    weightSuccess,
    selectedWeightMemberId,
    setSelectedWeightMemberId,
    weightValuesByMember,
    setWeightValuesByMember,
    weightYear,
    setWeightYear,
    weightPeriod,
    setWeightPeriod,
    isSubmittingWeight,
    deletingWeightId,
    editingWeightId,
    isWeightEditModalOpen,
    setIsWeightEditModalOpen,
    editWeightMemberId,
    setEditWeightMemberId,
    editWeightYear,
    setEditWeightYear,
    editWeightPeriod,
    setEditWeightPeriod,
    editWeightValue,
    setEditWeightValue,
    isUpdatingWeight,
    filteredWeightEntries,
    weightChartData,
    weightChartOptions,
    weightChartAvatarPlugin,
    latestWeightEntriesByMember,
    weightHomeHighlight,
    weightHomeGainHighlight,
    pastWeightWinners,
    handleCreateWeight,
    canDeleteWeightEntry,
    canEditWeightEntry,
    handleStartEditWeight,
    handleCancelEditWeight,
    handleSaveEditedWeight,
    handleDeleteWeight,
  } = useWeights(currentUser, hasAppAccess, isAdmin, members, memberNameById, memberById, memberAvatarById);

  if (isLoadingUser) {
    return (
      <main className="flex min-h-screen items-center justify-center px-4 py-10">
        <Card className="w-full max-w-md rounded-3xl bg-white/90 p-8 shadow-card animate-riseIn">
          <Flex direction="column" gap="2">
            <Text size="1" weight="bold" className="uppercase tracking-[0.22em] text-pine-700">
              DeFedeSvin
            </Text>
            <Text size="3" weight="medium" className="inline-flex items-center gap-2">
              <MaterialIcon name="hourglass_top" className="text-[20px] text-pine-700" />
              Tjekker session...
            </Text>
          </Flex>
        </Card>
      </main>
    );
  }

  if (currentUser) {
    return (
      <main className="min-h-screen px-4 py-8 sm:py-10">
        <div className="mx-auto w-full max-w-6xl">
          <header className="mb-5 rounded-3xl border border-white/40 bg-white/90 p-5 shadow-card">
            <Flex justify="between" align="start" gap="4" wrap="wrap">
              <Box>
                <Text size="1" weight="bold" className="uppercase tracking-[0.24em] text-pine-600">
                  De Fede Svin
                </Text>
                <Heading size="8" className="mt-1 font-display text-pine-900">
                  <span className="inline-flex items-center gap-2">
                    <MaterialIcon name="sports_bar" filled className="text-[32px] text-amber-600" />
                    Velkommen til De Fede Svin
                  </span>
                </Heading>
                <Text size="2" color="gray" className="mt-2 block">
                  {currentUser.email || 'Bruger'}
                </Text>
              </Box>

              <Flex gap="2" align="center" wrap="wrap" className="sm:justify-end">
                <Button
                  type="button"
                  onClick={() => setView(view === 'admin' ? 'home' : 'admin')}
                  disabled={!isAdmin || isCheckingAdmin}
                  variant={isAdmin ? 'solid' : 'soft'}
                  color="gray"
                  highContrast
                >
                  <span className="inline-flex items-center gap-1.5">
                    {isCheckingAdmin ? (
                      <MaterialIcon name="hourglass_top" className="text-[18px]" />
                    ) : view === 'admin' ? (
                      <MaterialIcon name="home" className="text-[18px]" />
                    ) : (
                      <MaterialIcon name="admin_panel_settings" className="text-[18px]" />
                    )}
                    {isCheckingAdmin ? 'Tjekker admin...' : view === 'admin' ? 'Til Hjem' : 'Åbn Adminpanel'}
                  </span>
                </Button>

                <Button
                  type="button"
                  onClick={handleSignOut}
                  disabled={isSubmitting}
                  variant="soft"
                  color="gray"
                  highContrast
                >
                  <span className="inline-flex items-center gap-1.5">
                    <MaterialIcon name="logout" className="text-[18px]" />
                    Log ud
                  </span>
                </Button>
              </Flex>
            </Flex>
          </header>

          {view === 'admin' ? (
            <Suspense fallback={<TabLoadingFallback />}>
              <AdminPanel
                managedUsers={managedUsers}
                members={members}
                isLoadingManagedUsers={isLoadingManagedUsers}
                adminError={adminError}
                adminSuccess={adminSuccess}
                onRefreshUsers={fetchManagedUsers}
                onSetWhitelist={(uid, whitelisted) => void handleSetWhitelist(uid, whitelisted)}
                onSetMemberLink={(uid, linkedMemberId) => void handleSetUserMemberLink(uid, linkedMemberId)}
                onDeleteUser={(uid, emailValue) => void handleDeleteUser(uid, emailValue)}
              />
            </Suspense>
          ) : (
            <Card className="rounded-3xl bg-white/95 p-6 shadow-card sm:p-8 animate-riseIn">
              {whitelistStatus === 'loading' && (
                <Callout.Root color="gray" variant="soft" className="mb-4">
                  <Callout.Text>Validerer whitelist-status...</Callout.Text>
                </Callout.Root>
              )}

              {hasAppAccess && (
                <Tabs.Root defaultValue="hjem">
                  <Tabs.List>
                    <Tabs.Trigger value="hjem">
                      <span className="inline-flex items-center gap-1.5">
                        <MaterialIcon name="home" className="text-[18px]" />
                        Hjem
                      </span>
                    </Tabs.Trigger>
                    <Tabs.Trigger value="tidligerevindere">
                      <span className="inline-flex items-center gap-1.5">
                        <MaterialIcon name="history" className="text-[18px]" />
                        Tidligere vindere
                      </span>
                    </Tabs.Trigger>
                    <Tabs.Trigger value="kalender">
                      <span className="inline-flex items-center gap-1.5">
                        <MaterialIcon name="calendar_month" className="text-[18px]" />
                        Kalender
                      </span>
                    </Tabs.Trigger>
                    <Tabs.Trigger value="medlemmer">
                      <span className="inline-flex items-center gap-1.5">
                        <MaterialIcon name="groups" className="text-[18px]" />
                        Medlemmer
                      </span>
                    </Tabs.Trigger>
                    <Tabs.Trigger value="vaegt">
                      <span className="inline-flex items-center gap-1.5">
                        <MaterialIcon name="monitor_weight" className="text-[18px]" />
                        Vægt
                      </span>
                    </Tabs.Trigger>
                    <Tabs.Trigger value="referat">
                      <span className="inline-flex items-center gap-1.5">
                        <MaterialIcon name="description" className="text-[18px]" />
                        Referat
                      </span>
                    </Tabs.Trigger>
                    <Tabs.Trigger value="vedtaegter">
                      <span className="inline-flex items-center gap-1.5">
                        <MaterialIcon name="gavel" className="text-[18px]" />
                        Vedtægter
                      </span>
                    </Tabs.Trigger>
                    <Tabs.Trigger value="forslag">
                      <span className="inline-flex items-center gap-1.5">
                        <MaterialIcon name="lightbulb" className="text-[18px]" />
                        Forslag
                      </span>
                    </Tabs.Trigger>
                    <Tabs.Trigger value="billedegalleri">
                      <span className="inline-flex items-center gap-1.5">
                        <MaterialIcon name="photo_library" className="text-[18px]" />
                        Billede Galleri
                      </span>
                    </Tabs.Trigger>
                  </Tabs.List>

                  <Box pt="4">
                    <Tabs.Content value="hjem">
                      <HomeTab
                        isAdmin={isAdmin}
                        userEmail={currentUser.email || 'bruger'}
                        weightHighlight={weightHomeHighlight}
                        weightGainHighlight={weightHomeGainHighlight}
                      />
                    </Tabs.Content>

                    <Tabs.Content value="tidligerevindere">
                      <PastWinnersTab pastWinners={pastWeightWinners} />
                    </Tabs.Content>

                    <Tabs.Content value="kalender">
                      <Suspense fallback={<TabLoadingFallback />}>
                        <CalendarSection currentUser={currentUser} hasAppAccess={hasAppAccess} />
                      </Suspense>
                    </Tabs.Content>

                    <Tabs.Content value="medlemmer">
                      <MembersTab
                        isAdmin={isAdmin}
                        members={members}
                        isLoadingMembers={isLoadingMembers}
                        membersError={membersError}
                        memberSuccess={memberSuccess}
                        memberName={memberName}
                        setMemberName={setMemberName}
                        memberRole={memberRole}
                        setMemberRole={setMemberRole}
                        memberDescription={memberDescription}
                        setMemberDescription={setMemberDescription}
                        setMemberImage={setMemberImage}
                        isSubmittingMember={isSubmittingMember}
                        handleCreateMember={handleCreateMember}
                        validateImageFile={validateImageFile}
                        setMembersError={setMembersError}
                        editingMemberId={editingMemberId}
                        editName={editName}
                        setEditName={setEditName}
                        editRole={editRole}
                        setEditRole={setEditRole}
                        editDescription={editDescription}
                        setEditDescription={setEditDescription}
                        setEditImage={setEditImage}
                        isUpdatingMember={isUpdatingMember}
                        isDeletingMember={isDeletingMember}
                        handleUpdateMember={handleUpdateMember}
                        cancelEditMember={cancelEditMember}
                        startEditMember={startEditMember}
                        handleDeleteMember={handleDeleteMember}
                        getRoleLabel={getRoleLabel}
                      />
                    </Tabs.Content>

                    <Tabs.Content value="vaegt">
                      <Suspense fallback={<TabLoadingFallback />}>
                        <WeightTab
                        members={members}
                        selectedWeightMemberId={selectedWeightMemberId}
                        setSelectedWeightMemberId={setSelectedWeightMemberId}
                        isLoadingWeights={isLoadingWeights}
                        weightChartData={weightChartData}
                        weightChartOptions={weightChartOptions}
                        weightChartPlugins={[weightChartAvatarPlugin]}
                        filteredWeightEntries={filteredWeightEntries}
                        handleCreateWeight={handleCreateWeight}
                        weightValuesByMember={weightValuesByMember}
                        setWeightValuesByMember={setWeightValuesByMember}
                        weightYear={weightYear}
                        setWeightYear={setWeightYear}
                        weightPeriod={weightPeriod}
                        setWeightPeriod={setWeightPeriod}
                        isSubmittingWeight={isSubmittingWeight}
                        weightsError={weightsError}
                        weightSuccess={weightSuccess}
                        latestWeightEntriesByMember={latestWeightEntriesByMember}
                        canEditWeightEntry={canEditWeightEntry}
                        handleStartEditWeight={handleStartEditWeight}
                        canDeleteWeightEntry={canDeleteWeightEntry}
                        deletingWeightId={deletingWeightId}
                        editingWeightId={editingWeightId}
                        isWeightEditModalOpen={isWeightEditModalOpen}
                        setIsWeightEditModalOpen={setIsWeightEditModalOpen}
                        handleSaveEditedWeight={handleSaveEditedWeight}
                        handleCancelEditWeight={handleCancelEditWeight}
                        editWeightMemberId={editWeightMemberId}
                        setEditWeightMemberId={setEditWeightMemberId}
                        editWeightYear={editWeightYear}
                        setEditWeightYear={setEditWeightYear}
                        editWeightPeriod={editWeightPeriod}
                        setEditWeightPeriod={setEditWeightPeriod}
                        editWeightValue={editWeightValue}
                        setEditWeightValue={setEditWeightValue}
                        isUpdatingWeight={isUpdatingWeight}
                        handleDeleteWeight={handleDeleteWeight}
                        />
                      </Suspense>
                    </Tabs.Content>

                    <Tabs.Content value="referat">
                      <Suspense fallback={<TabLoadingFallback />}>
                        <ReferatSection
                          currentUser={currentUser}
                          hasAppAccess={hasAppAccess}
                          isAdmin={isAdmin}
                        />
                      </Suspense>
                    </Tabs.Content>

                    <Tabs.Content value="vedtaegter">
                      <VedtaegterTab
                        hasAppAccess={hasAppAccess}
                        currentUserId={currentUser?.uid || null}
                      />
                    </Tabs.Content>

                    <Tabs.Content value="forslag">
                      <Suspense fallback={<TabLoadingFallback />}>
                        <SuggestionsSection
                          currentUser={currentUser}
                          hasAppAccess={hasAppAccess}
                          isAdmin={isAdmin}
                          memberNameById={memberNameById}
                        />
                      </Suspense>
                    </Tabs.Content>

                    <Tabs.Content value="billedegalleri">
                      <Suspense fallback={<TabLoadingFallback />}>
                        <GallerySection
                          currentUser={currentUser}
                          hasAppAccess={hasAppAccess}
                          isAdmin={isAdmin}
                        />
                      </Suspense>
                    </Tabs.Content>
                  </Box>
                </Tabs.Root>
              )}

              {whitelistStatus === 'pending' && !isAdmin && (
                <>
                  <Heading size="7" className="font-display text-pine-900">
                    <span className="inline-flex items-center gap-2">
                      <MaterialIcon name="lock_clock" className="text-[28px] text-amber-600" />
                      Adgang afventer
                    </span>
                  </Heading>
                  <Text size="2" color="gray" className="mt-2 block">
                    Din konto er logget ind, men afventer admin-whitelisting.
                  </Text>
                  <Button
                    type="button"
                    onClick={handleRefreshAccess}
                    disabled={isSubmitting}
                    variant="soft"
                    color="gray"
                    highContrast
                    className="mt-4"
                  >
                    <span className="inline-flex items-center gap-1.5">
                      <MaterialIcon name="refresh" className="text-[18px]" />
                      Opdater adgang
                    </span>
                  </Button>
                </>
              )}

              {authError && (
                <Callout.Root color="red" variant="soft" className="mt-4">
                  <Callout.Text>{authError}</Callout.Text>
                </Callout.Root>
              )}
            </Card>
          )}
        </div>
      </main>
    );
  }

  return (
    <main className="flex min-h-screen items-center justify-center px-4 py-10">
      <Card className="w-full max-w-md rounded-3xl bg-white/95 p-7 shadow-card sm:p-8 animate-riseIn">
        <>
          <div className="mb-6">
            <Text size="1" weight="bold" className="uppercase tracking-[0.24em] text-pine-600">
              DeFedeSvin
            </Text>
            <Heading size="8" className="mt-1 font-display leading-tight text-pine-900">
              <span className="inline-flex items-center gap-2">
                <MaterialIcon name="waving_hand" className="text-[32px] text-amber-500" />
                Velkommen tilbage
              </span>
            </Heading>
          </div>
          <Text size="2" color="gray" className="mb-5">
            {isRegisterMode ? 'Opret din konto' : 'Log ind for at fortsaette'}
          </Text>

          <form className="grid gap-3" onSubmit={handleEmailAuth}>
            <label htmlFor="email" className="text-sm font-semibold text-slate-700">
              E-mail
            </label>
            <TextField.Root
              id="email"
              type="email"
              autoComplete="email"
              placeholder="dig@eksempel.dk"
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              required
            />

            <label htmlFor="password" className="text-sm font-semibold text-slate-700">
              Adgangskode
            </label>
            <TextField.Root
              id="password"
              type="password"
              autoComplete={isRegisterMode ? 'new-password' : 'current-password'}
              placeholder="Indtast din adgangskode"
              value={password}
              onChange={(event) => setPassword(event.target.value)}
              required
            />

            <Button
              type="submit"
              disabled={isSubmitting}
              variant="solid"
              color="grass"
              highContrast
              className="mt-1"
            >
              <span className="inline-flex items-center gap-1.5">
                <MaterialIcon
                  name={isSubmitting ? 'hourglass_top' : isRegisterMode ? 'person_add' : 'login'}
                  className="text-[18px]"
                />
                {isSubmitting ? 'Arbejder...' : isRegisterMode ? 'Opret konto' : 'Log ind'}
              </span>
            </Button>
          </form>

          <Button
            type="button"
            onClick={handleGoogleSignIn}
            disabled={isSubmitting}
            variant="soft"
            color="gray"
            highContrast
            className="mt-3 w-full"
          >
            <span className="inline-flex items-center gap-1.5">
              <MaterialIcon name="login" className="text-[18px]" />
              Fortsaet med Google
            </span>
          </Button>

          <Button
            type="button"
            onClick={() => {
              setIsRegisterMode((currentMode) => !currentMode);
              setAuthError('');
            }}
            disabled={isSubmitting}
            variant="ghost"
            color="gray"
            className="mt-2 w-full"
          >
            <span className="inline-flex items-center gap-1.5">
              <MaterialIcon
                name={isRegisterMode ? 'login' : 'person_add'}
                className="text-[18px]"
              />
              {isRegisterMode
                ? 'Har du allerede en konto? Log ind'
                : 'Har du ikke en konto? Opret en'}
            </span>
          </Button>
        </>

        {authError && (
          <Callout.Root color="red" variant="soft" className="mt-4">
            <Callout.Text>{authError}</Callout.Text>
          </Callout.Root>
        )}
      </Card>
    </main>
  );
}

export default App;
